import { Button } from "@tokenterminal/core/ui/button/Button"
import { Combobox } from "@tokenterminal/core/ui/combobox/Combobox"
import { ComboboxWithPinnedItems } from "@tokenterminal/core/ui/combobox/ComboboxWithPinnedItems"
import { ProjectLogo } from "components/project/ProjectLogo"
import useChains from "hooks/trpc/useChains"
import { useAtom } from "jotai"
import { LogoStack } from "modules/market-sectors/DetailPage/MetricsTab/ChainsDeployedOnLinkOrModal"
import { memo, useCallback, useMemo } from "react"
import { twMerge } from "tailwind-merge"
import { focusAtom } from "utils/jotai/focusAtom"
import { type FiltersAtomType } from "../ScreenerTable"
import { FilterTypeText, SelectedFilterBase } from "./SelectedFilterBase"

type ChainDropdownProps = {
  filtersAtom: FiltersAtomType
}

type SelectedChainType = {
  id: string
  name: string
  logo: string
  slug: string | null
}

const Label = memo(function Label({
  selected,
  limit = 2,
}: {
  selected?: Array<SelectedChainType>
  limit?: number
}) {
  if (!selected || selected.length === 0) {
    return "Filter by chain"
  }

  if (selected.length === 1) {
    const label = selected[0]

    return (
      <div className="flex items-center gap-1">
        <LogoStack
          size={14}
          projectLogos={selected
            .map((chain) => ({
              logoUrl: chain.logo,
              projectName: chain.name,
            }))
            .slice(0, 3)}
        />
        {label.name}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <LogoStack
        size={14}
        projectLogos={selected
          .map((chain) => ({
            logoUrl: chain.logo,
            projectName: chain.name,
          }))
          .slice(0, 3)}
      />
      <div className="flex items-center gap-1">
        {selected.slice(0, limit).map((chain, i) => (
          <span key={chain.id}>
            {chain.name}
            {i < selected.length - 1 && ", "}
          </span>
        ))}
        {selected.length > limit && (
          <span>and {selected.length - limit} more</span>
        )}
      </div>
    </div>
  )
})

export function ChainDropdown({ filtersAtom, ...props }: ChainDropdownProps) {
  const [chains, setChains] = useAtom(focusAtom(filtersAtom, "chains"))
  const { data: { data: availableChains } = { data: [] } } = useChains()

  const uniqueChains = useMemo(() => {
    return availableChains
      ?.filter(
        (chain, index, self) =>
          index === self.findIndex((t) => t.name === chain.name)
      )
      .map((chain) => {
        return {
          id: chain.chain_id,
          name: chain.name,
          slug: chain.project_slug,
          label: chain.name,
          data_id: chain.project_data_id,
          symbol: chain.symbol,
          logo: chain.logo,
        }
      })
  }, [availableChains])

  const selectedChains = useMemo(() => {
    return uniqueChains?.filter((chain) => chains.includes(chain.id))
  }, [uniqueChains, chains])

  const items = useMemo(() => {
    return uniqueChains?.map((item) => ({
      id: item.id,
      value: item.label,
      keywords: item.symbol ? [item.symbol] : [],
      children: (
        <div className="flex gap-2 items-center justify-start">
          {item.logo ? (
            <ProjectLogo
              name={item.label}
              logo={item.logo}
              size={14}
              isRounded
              loading="eager"
            />
          ) : null}
          {item.label}
          {item.symbol && (
            <div
              className="text-sm text-fg-secondary"
              style={{ fontFamily: "var(--font-geist-mono)" }}
            >
              {item.symbol}
            </div>
          )}
        </div>
      ),
    }))
  }, [uniqueChains])

  const handleOnChangeSelectedIds = useCallback(
    (values: string[] | null) => {
      setChains(values ?? [])
    },
    [setChains]
  )

  if (chains?.length === 0) {
    return (
      <ComboboxWithPinnedItems
        drawerHeaderTitle="Select a chain"
        trigger={
          <Button
            size="small"
            variant="ghost"
            className="text-fg-secondary rounded-lg border border-dashed border-neutral-500/50"
          >
            Chain
            <Combobox.DropdownIcon />
          </Button>
        }
        placement="bottom left"
        items={items}
        closeOnSelect={false}
        selectedIds={chains}
        onChangeSelectedIds={handleOnChangeSelectedIds}
      />
    )
  }

  return (
    <SelectedFilterBase
      filterTypeSlot={<FilterTypeText>Chain</FilterTypeText>}
      operatorSlot={
        <div className="flex items-center h-full px-2 text-sm whitespace-nowrap">
          {selectedChains.length === 1 ? "is" : "is one of"}
        </div>
      }
      valueSlot={
        <ComboboxWithPinnedItems
          drawerHeaderTitle="Select a chain"
          trigger={
            <Button
              size="small"
              variant="ghost"
              className={twMerge(
                "!rounded-none border-none !h-full font-normal",
                !chains?.length &&
                  "rounded-lg border border-dashed border-neutral-500/50"
              )}
            >
              <Label selected={selectedChains} />
            </Button>
          }
          placement="bottom left"
          items={items}
          closeOnSelect={false}
          selectedIds={chains}
          onChangeSelectedIds={handleOnChangeSelectedIds}
        />
      }
      handleRemoveFilter={() => setChains([])}
    />
  )
}
